import { Box, ButtonBase, Typography, useTheme } from "@mui/material";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { csCZ, enUS as muiEnUS } from "@mui/x-date-pickers/locales";
import { format, isValid, type Locale } from "date-fns";
import { cs, enUS } from "date-fns/locale";
import { useState } from "react";
import {
	type Control,
	Controller,
	type FieldPath,
	type FieldValues,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { VALUE_LINE_HEIGHT } from "web_myplann/Shared/Components/FlatRow/constants";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type DateView = "year" | "month" | "day";

type Props<
	FormType extends FieldValues,
	TName,
	TContext,
	TTransformed extends FieldValues | undefined,
> = {
	control: Control<FormType, TContext, TTransformed>;
	name: TName;
	label: string;
	views?: DateView[];
	displayFormat?: string;
	minDate?: Date;
	maxDate?: Date;
	isLast?: boolean;
};

export const FlatRowFormDatePicker = <
	FormType extends FieldValues,
	TName extends FieldPath<FormType>,
	TContext = unknown,
	TTransformed extends FieldValues | undefined = undefined,
>({
	control,
	name,
	label,
	views = ["year", "month"],
	displayFormat = DateFormat.MonthYearLong,
	minDate,
	maxDate,
	isLast,
}: Props<FormType, TName, TContext, TTransformed>) => {
	const theme = useTheme();
	const { i18n } = useTranslation();
	const [open, setOpen] = useState(false);

	const isEnglish = i18n.language?.toLowerCase().startsWith("en");
	const dateFnsLocale: Locale = isEnglish ? enUS : cs;
	const muiLocaleText = (isEnglish ? muiEnUS : csCZ).components
		.MuiLocalizationProvider.defaultProps.localeText;

	return (
		<Controller
			control={control}
			name={name}
			render={({
				field: { value, onChange, onBlur },
				fieldState: { error },
			}) => {
				const rawValue = value as unknown;
				const dateValue: Date | null =
					rawValue instanceof Date && isValid(rawValue) ? rawValue : null;

				const displayValue = dateValue
					? format(dateValue, displayFormat, { locale: dateFnsLocale })
					: "";

				const coerceToDate = (next: unknown): Date | null => {
					if (next == null) return null;
					if (next instanceof Date) return isValid(next) ? next : null;
					if (
						typeof next === "object" &&
						"toDate" in next &&
						typeof (next as { toDate: () => Date }).toDate === "function"
					) {
						const asDate = (next as { toDate: () => Date }).toDate();
						return isValid(asDate) ? asDate : null;
					}
					return null;
				};

				const handlePick = (next: unknown) => {
					const coerced = coerceToDate(next);
					onChange(coerced ?? undefined);
				};

				const handleAccept = (next: unknown) => {
					handlePick(next);
					setOpen(false);
				};

				const handleClose = () => {
					setOpen(false);
					onBlur();
				};

				return (
					<Box
						borderBottom={isLast ? "none" : theme.colors.border.flatRowDivider}
					>
						<ButtonBase
							onClick={() => setOpen(true)}
							sx={{
								width: "100%",
								px: 1.6,
								py: 1.4,
								minHeight: 48,
								justifyContent: "space-between",
								alignItems: "flex-start",
								"&:active": { bgcolor: theme.palette.deprecated.grey[50] },
							}}
						>
							<Typography
								variant="deprecatedBody1"
								color={
									error?.message
										? theme.palette.deprecated.error.main
										: theme.palette.deprecated.grey[500]
								}
								flexShrink={0}
								mr={2}
								lineHeight={`${VALUE_LINE_HEIGHT}px`}
							>
								{label}
							</Typography>
							<Typography
								variant="deprecatedBodyMedium"
								color={theme.palette.primary.main}
								minWidth={0}
								textAlign="right"
								textTransform="capitalize"
								lineHeight={`${VALUE_LINE_HEIGHT}px`}
								sx={{ overflowWrap: "anywhere" }}
							>
								{displayValue}
							</Typography>
						</ButtonBase>

						{error?.message && (
							<Typography
								component="p"
								variant="deprecatedCaption"
								color={theme.palette.deprecated.error.main}
								px={1.6}
								pb={0.6}
							>
								{error.message}
							</Typography>
						)}

						<LocalizationProvider
							dateAdapter={AdapterDateFns}
							adapterLocale={dateFnsLocale}
							localeText={muiLocaleText}
						>
							<MobileDatePicker
								open={open}
								onClose={handleClose}
								onChange={handlePick}
								onAccept={handleAccept}
								value={dateValue}
								views={views}
								minDate={minDate}
								maxDate={maxDate}
								closeOnSelect
								slotProps={{
									textField: { sx: { display: "none" } },
									mobilePaper: { className: "react-module" },
								}}
							/>
						</LocalizationProvider>
					</Box>
				);
			}}
		/>
	);
};
